/**
 * Como um candidato a ligação aparece: as duas telas de incluir ligações (a do
 * diagrama e a de um componente) mostram o mesmo dado do mesmo jeito.
 *
 * O legado (`IncluirLigacoes.cshtml`) era uma tabela com o componente, o pino
 * principal, o circuito e o pino do componente, em ordem de pino principal. A
 * tela nova chegou mostrando "Componente 96818", que não diz qual componente é.
 * Aqui volta o que o legado mostrava, e mais o conector e a cor, que separam
 * pinos de mesmo nome.
 *
 * **O nome é o apelido.** O `Componente.ToString()` do legado, no endereço por
 * onde a equipe usava o Sistema, mostrava o apelido ("Motor da Borboleta
 * Eletrônica") e, sem apelido, "id - descrição" ("96643 - Malha"). Aqui o
 * apelido continua sendo o nome, com "código - descrição" ao lado; sem apelido,
 * o nome é "código - descrição" ("MA - Malha") — o id não diz nada a quem lê.
 */

import { chaveDoPar, type ComponenteCandidato, type PinoCandidato } from "@/api/ligacoesEmLote"

const porTexto = (a: string, b: string) => a.localeCompare(b, "pt-BR", { numeric: true })

type NomeDoComponente = Pick<
  ComponenteCandidato, "componenteId" | "componenteCodigo" | "componenteDescricao" | "componenteApelido">

/**
 * "código - descrição", sem repetir quando os dois são o mesmo texto (há
 * componente com a descrição copiada no código). Sem nenhum dos dois, o id.
 */
export function identificacaoDoCandidato(c: NomeDoComponente): string {
  const codigo = c.componenteCodigo?.trim()
  const descricao = c.componenteDescricao?.trim()
  if (codigo && descricao && codigo !== descricao) return `${codigo} - ${descricao}`
  return codigo || descricao || `Componente ${c.componenteId}`
}

/** O nome do componente: o apelido; sem apelido, "código - descrição". */
export function nomeDoCandidato(c: NomeDoComponente): string {
  return c.componenteApelido?.trim() || identificacaoDoCandidato(c)
}

/**
 * O nome em destaque e, quando o nome é o apelido, "código - descrição" menor
 * ao lado — quem conhece o componente pelo código também o acha.
 */
export function NomeDoCandidato({ candidato }: { candidato: NomeDoComponente }) {
  const nome = nomeDoCandidato(candidato)
  const identificacao = identificacaoDoCandidato(candidato)
  return (
    <>
      <b>{nome}</b>
      {nome !== identificacao && (
        <span style={{ color: "var(--txt3)", fontSize: 10.5 }}>{identificacao}</span>
      )}
    </>
  )
}

/** A ordem do legado: pelo pino principal, nos componentes e dentro de cada um. */
export function emOrdemDePinoPrincipal(itens: ComponenteCandidato[]): ComponenteCandidato[] {
  const comPinos = itens.map((c) => ({
    ...c, ligacoes: [...c.ligacoes].sort((a, b) => porTexto(a.pinoPrincipal ?? "", b.pinoPrincipal ?? "")),
  }))
  return comPinos.sort((a, b) =>
    porTexto(a.ligacoes[0]?.pinoPrincipal ?? "", b.ligacoes[0]?.pinoPrincipal ?? ""))
}

const COLUNAS = "18px minmax(56px, auto) 12px minmax(56px, auto) minmax(90px, 1.4fr) minmax(70px, 1fr) minmax(50px, 0.8fr)"

const celula: React.CSSProperties = { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }

/**
 * Os pinos de um candidato, uma linha por ligação: marcar, pino principal → pino
 * do componente, circuito, conector e cor.
 */
export function PinosDoCandidato({ ligacoes, marcado, aoAlternar, rotuloDoInicio = "Pino principal" }: {
  ligacoes: PinoCandidato[]
  marcado: (linha: PinoCandidato) => boolean
  aoAlternar: (linha: PinoCandidato) => void
  /** Na inclusão de um componente, o início é o pino dele, não o do principal. */
  rotuloDoInicio?: string
}) {
  return (
    <div role="table" style={{ display: "grid", gridTemplateColumns: COLUNAS, columnGap: 8, rowGap: 1, fontSize: 11 }}>
      <div role="row" style={{ display: "contents", color: "var(--txt3)", fontSize: 10 }}>
        <span />
        <span role="columnheader" style={celula}>{rotuloDoInicio}</span>
        <span />
        <span role="columnheader" style={celula}>Pino</span>
        <span role="columnheader" style={celula}>Circuito</span>
        <span role="columnheader" style={celula}>Conector</span>
        <span role="columnheader" style={celula}>Cor</span>
      </div>

      {ligacoes.map((l) => {
        const ligado = marcado(l)
        const fundo = ligado ? "var(--surface3)" : "transparent"
        const linha: React.CSSProperties = { ...celula, background: fundo, padding: "2px 0", cursor: "pointer" }
        return (
          <label
            key={chaveDoPar(l)}
            role="row"
            title={`pinout ${l.pinoutId} → ${l.ligacaoPinoutId}`}
            style={{ display: "contents" }}
          >
            <span style={{ ...linha, display: "flex", alignItems: "center" }}>
              <input type="checkbox" checked={ligado} onChange={() => aoAlternar(l)} />
            </span>
            <span className="mono" style={{ ...linha, fontWeight: 600 }}>{l.pinoPrincipal?.trim() || "—"}</span>
            <span style={{ ...linha, color: "var(--txt3)" }}>→</span>
            <span className="mono" style={{ ...linha, fontWeight: 600 }}>{l.pino.trim() || "(sem pino)"}</span>
            <span style={linha} title={l.circuitoApelido ? `${l.circuito} · ${l.circuitoApelido}` : l.circuito}>
              <span className="mono">{l.circuito || "—"}</span>
              {l.circuitoApelido && <span style={{ color: "var(--txt3)" }}> · {l.circuitoApelido}</span>}
            </span>
            <span style={{ ...linha, color: "var(--txt2)" }}>{l.conector || "—"}</span>
            <span style={{ ...linha, color: "var(--txt2)" }}>{l.cor || "—"}</span>
          </label>
        )
      })}
    </div>
  )
}
